import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { ArrowRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useAdminIssueTransitions } from '../../../hooks/useAdminIssueTransitions';
import { useTransitionAdminIssue } from '../../../hooks/useTransitionAdminIssue';
import { statusCategoryClass } from './columns';
import type { AdminIssue } from '../../../api/adminIssuesApi';

// Only the fields the dialog actually renders: lets the admin detail page open
// it from a full issue payload without faking a complete AdminIssue row.
export type ValidateTransitionIssue = Pick<
  AdminIssue,
  'issueKey' | 'summary' | 'status' | 'statusCategory'
>;

interface ValidateTransitionDialogProps {
  issue: ValidateTransitionIssue | null;
  onClose: () => void;
}

// Lists the Jira transitions available at the issue's current gate and applies the chosen one.
export const ValidateTransitionDialog = ({ issue, onClose }: ValidateTransitionDialogProps) => {
  const { t } = useTranslation();
  const isOpen = issue != null;
  const { data: transitions, isLoading, error } = useAdminIssueTransitions(issue?.issueKey ?? null, isOpen);
  const { mutate, isPending, error: mutationError } = useTransitionAdminIssue();

  const handleTransition = (transitionId: string) => {
    if (!issue) return;
    mutate({ issueKey: issue.issueKey, transitionId }, { onSuccess: onClose });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Validate issue')}</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{issue?.issueKey}</span> — {issue?.summary}
          </DialogDescription>
        </DialogHeader>

        {issue ? (
          <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2">
            <span className="text-sm text-gray-500">{t('Current status')}</span>
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusCategoryClass[issue.statusCategory]}`}
            >
              {issue.status || '—'}
            </span>
          </div>
        ) : null}

        {isLoading ? (
          <div className="py-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{(error as Error).message}</p>
        ) : !transitions?.length ? (
          <p className="text-sm text-gray-500">{t('No transition available')}</p>
        ) : (
          <div className="flex flex-col gap-2 py-1">
            <span className="text-sm font-medium text-gray-700">{t('Choose an action')}</span>
            {transitions.map((tr) => (
              <Button
                key={tr.id}
                variant="outline"
                disabled={isPending}
                onClick={() => handleTransition(tr.id)}
                className="justify-between"
              >
                {tr.name}
                <ArrowRight className="w-4 h-4 opacity-60" />
              </Button>
            ))}
          </div>
        )}

        {mutationError ? (
          <p className="text-sm text-red-600">{(mutationError as Error).message}</p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
